import { createCollectionStore } from "../collection/collection-store.js";
import { createCompositeStore } from "../composite/composite-store.js";
import { batch, createStore, mergeStore, omit, setup, sync } from "@ariakit/store";
import { chain, defaultValue } from "@ariakit/utils";
//#region src/tab/tab-store.ts
function getFocusedTab(items) {
	const activeElement = items[0]?.element?.ownerDocument.activeElement;
	if (!activeElement) return;
	return items.find((item) => item.element === activeElement);
}
function getTabById(items, id) {
	if (id == null) return;
	return items.find((item) => item.id === id);
}
function isEnabledTab(item) {
	if (!item) return false;
	if (item.disabled) return false;
	if (item.dimmed) return false;
	return true;
}
function createPanelsStore() {
	const panels = createCollectionStore();
	const panelsByTabId = /* @__PURE__ */ new Map();
	sync(panels, ["items"], (state) => {
		panelsByTabId.clear();
		for (const panel of state.items) {
			const { tabId } = panel;
			if (tabId == null) continue;
			if (panelsByTabId.has(tabId)) continue;
			panelsByTabId.set(tabId, panel);
		}
	});
	return {
		panels,
		panel: (tabId) => {
			if (tabId == null) return null;
			return panelsByTabId.get(tabId) || null;
		}
	};
}
function createTabStore({ composite: parentComposite, combobox, ...props } = {}) {
	const independentKeys = [
		"items",
		"renderedItems",
		"moves",
		"orientation",
		"virtualFocus",
		"includesBaseElement",
		"baseElement",
		"focusLoop",
		"focusShift",
		"focusWrap"
	];
	const store = mergeStore(props.store, omit(parentComposite, independentKeys), omit(combobox, independentKeys));
	const syncState = store?.getState();
	const composite = createCompositeStore({
		...props,
		store,
		includesBaseElement: defaultValue(props.includesBaseElement, syncState?.includesBaseElement, false),
		orientation: defaultValue(props.orientation, syncState?.orientation, "horizontal"),
		focusLoop: defaultValue(props.focusLoop, syncState?.focusLoop, true)
	});
	const { panels, panel } = createPanelsStore();
	const tab = createStore({
		...composite.getState(),
		selectedId: defaultValue(props.selectedId, syncState?.selectedId, props.defaultSelectedId),
		selectOnMove: defaultValue(props.selectOnMove, syncState?.selectOnMove, true)
	}, composite, store);
	setup(tab, () => sync(tab, ["moves"], () => {
		const { activeId, selectOnMove } = tab.getState();
		if (!selectOnMove) return;
		if (!activeId) return;
		const tabItem = composite.item(activeId);
		if (!isEnabledTab(tabItem)) return;
		tab.setState("selectedId", tabItem.id);
	}));
	let pendingRestore = false;
	let restoredSelectedId;
	setup(tab, () => {
		pendingRestore = false;
		return batch(tab, ["selectedId"], (state, prev) => {
			if (pendingRestore) {
				pendingRestore = false;
				if (state.selectedId === restoredSelectedId) return;
			}
			if (parentComposite && state.selectedId === prev.selectedId) return;
			const { activeId, renderedItems } = tab.getState();
			const focusedTab = getFocusedTab(renderedItems);
			const selectedTab = getTabById(renderedItems, state.selectedId);
			if (focusedTab && isEnabledTab(selectedTab) && activeId !== selectedTab.id) {
				composite.move(selectedTab.id);
				return;
			}
			tab.setState("activeId", state.selectedId);
		});
	});
	setup(tab, () => sync(tab, ["selectedId", "renderedItems"], (state) => {
		if (state.selectedId !== void 0) return;
		const { activeId, renderedItems } = tab.getState();
		const tabItem = composite.item(activeId);
		if (isEnabledTab(tabItem)) tab.setState("selectedId", tabItem.id);
		else {
			const tabItem = renderedItems.find(isEnabledTab);
			tab.setState("selectedId", tabItem?.id);
		}
	}));
	setup(tab, () => sync(tab, ["renderedItems"], (state) => {
		const tabs = state.renderedItems;
		if (!tabs.length) return;
		return sync(panels, ["renderedItems"], (state) => {
			const items = state.renderedItems;
			if (!items.some((panel) => !panel.tabId)) return;
			items.forEach((panel, i) => {
				if (panel.tabId) return;
				const tabItem = tabs[i];
				if (!tabItem) return;
				panels.renderItem({
					...panel,
					tabId: tabItem.id
				});
			});
		});
	}));
	let selectedIdFromSelectedValue = null;
	setup(tab, () => {
		const backupSelectedId = () => {
			selectedIdFromSelectedValue = tab.getState().selectedId;
		};
		const restoreSelectedId = () => {
			const { selectedId } = tab.getState();
			if (selectedId === selectedIdFromSelectedValue) return;
			pendingRestore = true;
			restoredSelectedId = selectedIdFromSelectedValue;
			tab.setState("selectedId", selectedIdFromSelectedValue);
		};
		if (parentComposite && "setSelectElement" in parentComposite) return chain(sync(parentComposite, ["value"], backupSelectedId), sync(parentComposite, ["mounted"], restoreSelectedId));
		if (!combobox) return;
		return chain(sync(combobox, ["selectedValue"], backupSelectedId), sync(combobox, ["mounted"], restoreSelectedId));
	});
	return {
		...composite,
		...tab,
		panels,
		panel,
		setSelectedId: (id) => tab.setState("selectedId", id),
		select: (id) => {
			tab.setState("selectedId", id);
			composite.move(id);
		}
	};
}
//#endregion
export { createTabStore };

//# sourceMappingURL=tab-store.js.map